import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, User, Users } from 'lucide-react';

const GroupCreationModal = ({ isOpen, onClose, contacts, onCreateGroup, isCreating, currentUserId }) => {
  const [groupName, setGroupName] = useState('');
  const [selectedContacts, setSelectedContacts] = useState({});
  const [expandedClass, setExpandedClass] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setGroupName('');
      setSelectedContacts({});
      setExpandedClass(null);
    }
  }, [isOpen]);

  const toggleExpand = (classId) => {
    setExpandedClass(expandedClass === classId ? null : classId);
  };

  const handleContactSelect = (contact) => {
    setSelectedContacts(prev => ({
      ...prev,
      [contact.id]: prev[contact.id] ? undefined : contact
    }));
  };

  const handleCreateGroup = () => {
    if (groupName.trim() && Object.keys(selectedContacts).length > 0) {
      onCreateGroup(groupName, Object.values(selectedContacts).filter(Boolean));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Create New Group</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
            <span className="sr-only">Close</span>
          </button>
        </div>
        <input
          type="text"
          placeholder="Group Name"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          className="w-full px-3 py-2 border rounded-md mb-4"
        />
        <div className="mb-4 flex-grow overflow-y-auto">
          <h3 className="font-semibold mb-2">Select Participants:</h3>
          <div className="space-y-2">
            {contacts.map((classData, idx) => {
              const classId = `${classData.classValue}-${classData.section}`;
              const isExpanded = expandedClass === classId;

              // Filter out the current user from the teacher and students
              const showTeacher = classData.teacherId !== currentUserId;
              const filteredStudents = classData.enrolledStudent.filter(student => student.studentId !== currentUserId);

              if (!showTeacher && filteredStudents.length === 0) {
                return null; // Don't render this class if there are no contacts to show
              }

              return (
                <div key={idx} className="border rounded-lg overflow-hidden">
                  <button
                    className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={() => toggleExpand(classId)}
                  >
                    <span className="font-semibold">
                      Class {classData.classValue} - Section {classData.section}
                    </span>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {isExpanded && (
                    <div className="p-3 bg-white">
                      {showTeacher && (
                        <>
                          <h4 className="font-bold text-sm mb-2 flex items-center">
                            <User className="mr-2" size={16} /> Teacher
                          </h4>
                          <div className="mb-2">
                            <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                              <input
                                type="checkbox"
                                checked={!!selectedContacts[classData.teacherId]}
                                onChange={() => handleContactSelect({ id: classData.teacherId, name: classData.teacherName, type: 'teacher' })}
                                className="form-checkbox h-4 w-4 text-blue-600"
                              />
                              <span>{classData.teacherName}</span>
                            </label>
                          </div>
                        </>
                      )}
                      {filteredStudents.length > 0 && (
                        <>
                          <h4 className="font-bold text-sm mb-2 flex items-center">
                            <Users className="mr-2" size={16} /> Students
                          </h4>
                          <div className="space-y-1">
                            {filteredStudents.map((student) => (
                              <label key={student.studentId} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                <input
                                  type="checkbox"
                                  checked={!!selectedContacts[student.studentId]}
                                  onChange={() => handleContactSelect({ id: student.studentId, name: student.studentName, type: 'student' })}
                                  className="form-checkbox h-4 w-4 text-blue-600"
                                />
                                <span>{student.studentName}</span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <button
          onClick={handleCreateGroup}
          disabled={isCreating || !groupName.trim() || Object.keys(selectedContacts).length === 0}
          className={`w-full py-2 rounded-md ${
            isCreating || !groupName.trim() || Object.keys(selectedContacts).length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          {isCreating ? 'Creating Group...' : 'Create Group'}
        </button>
      </div>
    </div>
  );
};

export default GroupCreationModal;

